(() => {
  if (customElements.get('dio-compatibility-table')) return;
class DioCompatibilityTable extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    const search = this.querySelector('[data-compatibility-search]');
    const head = this.querySelector('[data-compatibility-head]');
    const body = this.querySelector('[data-compatibility-body]');
    const status = this.querySelector('[data-compatibility-status]');
    const empty = this.querySelector('[data-compatibility-empty]');
    const pagination = this.querySelector('[data-compatibility-pagination]');
    if (!search || !head || !body || !status || !empty || !pagination) return;

    this.sizeSearchToPlaceholder(search);
    document.fonts?.ready.then(() => this.sizeSearchToPlaceholder(search));

    let data;
    try {
      data = JSON.parse(this.dataset.table || 'null');
    } catch (_) {
      data = null;
    }

    const headers = Array.isArray(data?.headers) ? data.headers : [];
    const rows = Array.isArray(data?.rows) ? data.rows : [];
    const links = Array.isArray(data?.links) && data.links.length === rows.length ? data.links : [];
    if (!headers.length || !rows.every((row) => Array.isArray(row) && row.length === headers.length)) {
      this.querySelector('.dio-compatibility-table__controls').hidden = true;
      this.querySelector('[data-compatibility-scroll]').hidden = true;
      empty.textContent = this.dataset.unavailable || 'Table unavailable';
      empty.hidden = false;
      return;
    }

    const headerRow = document.createElement('tr');
    headers.forEach((header, index) => {
      const cell = document.createElement('th');
      cell.scope = 'col';
      cell.textContent = String(header);
      if (index > 0) cell.classList.add('dio-compatibility-table__price');
      headerRow.append(cell);
    });
    head.replaceChildren(headerRow);

    const indexedRows = rows.map((row, index) => ({
      row,
      link: this.productUrl(links[index]),
      index,
      price: this.priceValue(row[1]),
      prices: row.slice(1).map((value) => this.priceValue(value)),
      device: this.deviceName(row[0]).text.normalize('NFKD').toLocaleLowerCase(),
      searchable: [this.deviceName(row[0]).text, ...row.slice(1)].join(' ').normalize('NFKD').toLocaleLowerCase(),
    })).sort((a, b) => a.price === b.price ? a.index - b.index : a.price < b.price ? -1 : 1);

    const pageSize = 10;
    let currentPage = 1;

    const renderPagination = (pageCount) => {
      pagination.replaceChildren();
      pagination.hidden = pageCount <= 1;
      if (pageCount <= 1) return;

      const pages = pageCount <= 4
        ? Array.from({ length: pageCount }, (_, index) => index + 1)
        : [...new Set([
          1, pageCount, currentPage - 1, currentPage, currentPage + 1,
          ...(currentPage <= 2 ? [2, 3] : []),
          ...(currentPage >= pageCount - 1 ? [pageCount - 2, pageCount - 1] : []),
        ])].filter((page) => page >= 1 && page <= pageCount).sort((a, b) => a - b);
      const fragment = document.createDocumentFragment();
      const arrow = (page, direction, symbol) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'dio-compatibility-table__page-arrow';
        button.dataset.page = String(page);
        button.dataset.direction = direction;
        button.textContent = symbol;
        button.setAttribute('aria-label', `${direction === 'previous' ? 'Previous' : 'Next'} page`);
        return button;
      };
      if (currentPage > 1) fragment.append(arrow(currentPage - 1, 'previous', '‹'));
      let previousPage = 0;
      pages.forEach((page) => {
        if (previousPage && page > previousPage + 1) {
          const ellipsis = document.createElement('span');
          ellipsis.className = 'dio-compatibility-table__page-ellipsis';
          ellipsis.textContent = '…';
          ellipsis.setAttribute('aria-hidden', 'true');
          fragment.append(ellipsis);
        }
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.page = String(page);
        button.textContent = String(page);
        button.setAttribute('aria-label', `Page ${page} of ${pageCount}`);
        if (page === currentPage) button.setAttribute('aria-current', 'page');
        fragment.append(button);
        previousPage = page;
      });
      if (currentPage < pageCount) fragment.append(arrow(currentPage + 1, 'next', '›'));
      pagination.append(fragment);
    };

    const render = (announcement = '') => {
      const term = search.value.trim().normalize('NFKD').toLocaleLowerCase();
      const priceQuery = this.parsePriceQuery(term, headers);
      const matches = term
        ? indexedRows.filter((entry) => priceQuery
          ? entry.device.includes(priceQuery.device) && this.matchesPrice(entry.prices[priceQuery.column], priceQuery)
          : entry.searchable.includes(term))
        : indexedRows;
      const pageCount = Math.ceil(matches.length / pageSize);
      currentPage = Math.min(currentPage, Math.max(pageCount, 1));
      const visibleRows = matches.slice((currentPage - 1) * pageSize, currentPage * pageSize);
      const fragment = document.createDocumentFragment();
      visibleRows.forEach(({row, link}) => {
        const tr = document.createElement('tr');
        row.forEach((value, index) => {
          const cell = document.createElement(index === 0 ? 'th' : 'td');
          if (index === 0) cell.scope = 'row';
          if (index === 0 && link) {
            const anchor = document.createElement('a');
            anchor.className = 'dio-compatibility-table__device-link';
            anchor.href = link;
            this.renderDeviceName(anchor, value);
            cell.append(anchor);
          } else if (index === 0) {
            this.renderDeviceName(cell, value);
          } else {
            cell.textContent = String(value);
          }
          if (index > 0) cell.classList.add('dio-compatibility-table__price');
          tr.append(cell);
        });
        fragment.append(tr);
      });
      body.replaceChildren(fragment);
      empty.hidden = matches.length > 0;
      renderPagination(pageCount);
      if (announcement === 'search') status.textContent = matches.length ? this.dataset.resultsUpdated : empty.textContent;
      if (announcement === 'page') status.textContent = `Page ${currentPage} of ${pageCount}`;
    };

    search.addEventListener('input', () => {
      currentPage = 1;
      render('search');
    });
    pagination.addEventListener('click', (event) => {
      const button = event.target.closest('button[data-page]');
      if (!button || !pagination.contains(button)) return;
      const page = Number(button.dataset.page);
      if (!Number.isInteger(page) || page === currentPage) return;
      const direction = button.dataset.direction;
      currentPage = page;
      render('page');
      const focusTarget = (direction && pagination.querySelector(`[data-direction="${direction}"]`))
        || pagination.querySelector(`[data-page="${currentPage}"]`);
      focusTarget?.focus({ preventScroll: true });
    });
    render();
  }

  deviceName(value) {
    const text = String(value ?? '');
    const suffix = ' *(discontinued)*';
    return text.endsWith(suffix)
      ? { text: `${text.slice(0, -suffix.length)} (discontinued)`, model: text.slice(0, -suffix.length), discontinued: true }
      : { text, discontinued: false };
  }

  renderDeviceName(target, value) {
    const name = this.deviceName(value);
    if (!name.discontinued) {
      target.textContent = name.text;
      return;
    }
    const suffix = document.createElement('em');
    suffix.className = 'dio-compatibility-table__discontinued';
    suffix.textContent = '(discontinued)';
    target.append(document.createTextNode(`${name.model} `), suffix);
  }

  sizeSearchToPlaceholder(search) {
    const style = getComputedStyle(search);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return;
    context.font = style.font;
    const textWidth = context.measureText(search.placeholder).width;
    const spacing = style.letterSpacing === 'normal' ? 0 : parseFloat(style.letterSpacing) || 0;
    const sides = ['paddingLeft', 'paddingRight', 'borderLeftWidth', 'borderRightWidth']
      .reduce((total, key) => total + (parseFloat(style[key]) || 0), 0);
    search.style.setProperty('--dio-compatibility-search-width', `${Math.ceil(textWidth + spacing * search.placeholder.length + sides + 18)}px`);
  }

  productUrl(value) {
    if (typeof value !== 'string') return '';
    const url = value.trim();
    if (!url || /[\s\\\u0000-\u001f\u007f]/.test(url)) return '';
    if (url.startsWith('/products/')) return url;
    if (!url.startsWith('https://')) return '';
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'https:' && parsed.hostname && !parsed.username && !parsed.password ? url : '';
    } catch (_) {
      return '';
    }
  }

  parsePriceQuery(query, headers) {
    const amount = '(?:\\$\\s*)?(?:\\d{1,3}(?:,\\d{3})+|\\d+)(?:\\.\\d{1,2})?';
    const range = new RegExp(`(?:\\bbetween\\s+|\\bfrom\\s+)?(${amount})\\s*(?:-|–|—|\\bto\\b|\\band\\b)\\s*(${amount})(?![\\d,.])`, 'i');
    const comparison = new RegExp(`\\b(less than|under|below|up to|at most|no more than|greater than|more than|over|above|at least|no less than|minimum)\\s+(${amount})(?![\\d,.])`, 'i');
    const trailing = new RegExp(`(${amount})(?:\\s+(?:dollars?|bucks|usd))?\\s+(or less|or more)(?!\\w)`, 'i');
    const exact = new RegExp(`(?:^|\\s)(\\$\\s*${amount.replace('(?:\\$\\s*)?', '')})(?![\\d,.])`, 'i');
    const kind = query.match(range) ? 'range' : query.match(comparison) ? 'comparison' : query.match(trailing) ? 'trailing' : 'exact';
    const match = query.match({ range, comparison, trailing, exact }[kind]);
    if (!match) return null;

    const number = (value) => Number(value.replace(/[$,\s]/g, ''));
    let min = -Infinity;
    let max = Infinity;
    let minInclusive = true;
    let maxInclusive = true;
    if (kind === 'range') {
      min = Math.min(number(match[1]), number(match[2]));
      max = Math.max(number(match[1]), number(match[2]));
    } else if (kind === 'comparison') {
      const value = number(match[2]);
      if (/^(?:less than|under|below|up to|at most|no more than)$/i.test(match[1])) {
        max = value;
        maxInclusive = /^(?:up to|at most|no more than)$/i.test(match[1]);
      } else {
        min = value;
        minInclusive = /^(?:at least|no less than|minimum)$/i.test(match[1]);
      }
    } else if (kind === 'trailing') {
      const value = number(match[1]);
      if (match[2] === 'or less') max = value;
      else min = value;
    } else {
      min = number(match[1]);
      max = min;
    }
    if (Number.isNaN(min) || Number.isNaN(max)) return null;

    let device = query.replace(match[0], ' ');
    const pack = device.match(/\b([13])[\s-]*pack\b/i);
    const column = pack ? headers.findIndex((header) => new RegExp(`\\b${pack[1]}[\\s-]*pack\\b`, 'i').test(header)) - 1 : 0;
    if (pack) device = device.replace(pack[0], ' ');
    device = device.replace(/\b(?:price|prices|priced|for|dollars?|bucks|usd)\b/g, ' ').replace(/^\s*and\b|\band\s*$/g, ' ').replace(/\s+/g, ' ').trim();
    return { column: Math.max(column, 0), device, min, max, minInclusive, maxInclusive };
  }

  matchesPrice(price, query) {
    return Number.isFinite(price)
      && (query.minInclusive ? price >= query.min : price > query.min)
      && (query.maxInclusive ? price <= query.max : price < query.max);
  }

  priceValue(value) {
    const normalized = String(value ?? '').trim().replace(/[$,\s]/g, '');
    const price = Number(normalized);
    return normalized && Number.isFinite(price) && price >= 0 ? price : Number.POSITIVE_INFINITY;
  }
}

if (!customElements.get('dio-compatibility-table')) {
  customElements.define('dio-compatibility-table', DioCompatibilityTable);
}

})();
